import { FormEvent, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// Cognito attribute -> label for the NEW_PASSWORD_REQUIRED step's extra fields.
const ATTRIBUTE_LABELS: Record<string, string> = { given_name: 'First name', family_name: 'Last name' };

export function LoginPage() {
  const { signIn, completeNewPassword, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Set once Cognito answers the temporary password with NEW_PASSWORD_REQUIRED.
  const [missingAttributes, setMissingAttributes] = useState<string[] | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [attributes, setAttributes] = useState<Record<string, string>>({});

  if (isAuthenticated) {
    const from = (location.state as { from?: Location })?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await signIn(email, password);
      if (result.step === 'NEW_PASSWORD_REQUIRED') {
        setMissingAttributes(result.missingAttributes);
        return;
      }
      navigate('/', { replace: true });
    } catch {
      setError('Incorrect email or password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewPassword = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setIsSubmitting(true);
    try {
      await completeNewPassword(newPassword, attributes);
      navigate('/', { replace: true });
    } catch (err) {
      // Cognito's own message names the failed policy rule (length, symbol...).
      setError(err instanceof Error && err.message ? err.message : 'Could not set the new password.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (missingAttributes) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <form onSubmit={handleNewPassword} className="w-full max-w-sm space-y-4 rounded-lg border border-border p-8">
          <h1 className="text-lg font-semibold text-foreground">Set your password</h1>
          <p className="text-sm text-muted-foreground">Your temporary password worked. Choose a new one to finish signing in.</p>

          {missingAttributes.map((name) => (
            <div key={name} className="space-y-1">
              <label htmlFor={name} className="text-sm text-muted-foreground">
                {ATTRIBUTE_LABELS[name] ?? name}
              </label>
              <input
                id={name}
                type="text"
                required
                value={attributes[name] ?? ''}
                onChange={(e) => setAttributes({ ...attributes, [name]: e.target.value })}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </div>
          ))}

          <div className="space-y-1">
            <label htmlFor="new-password" className="text-sm text-muted-foreground">
              New password
            </label>
            <input
              id="new-password"
              type="password"
              required
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="confirm-password" className="text-sm text-muted-foreground">
              Confirm new password
            </label>
            <input
              id="confirm-password"
              type="password"
              required
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {isSubmitting ? 'Saving…' : 'Set password and sign in'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm space-y-4 rounded-lg border border-border p-8"
      >
        <h1 className="text-lg font-semibold text-foreground">Admin sign in</h1>

        <div className="space-y-1">
          <label htmlFor="email" className="text-sm text-muted-foreground">
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="password" className="text-sm text-muted-foreground">
            Password
          </label>
          <input
            id="password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
